export type ProjectResourceAccessLevel = "DISABLED" | "READ" | "WRITE";
export type ProjectResourceUiAccess = "no_access" | "read" | "action";
export type ProjectAssignmentCeiling = "DISABLED" | "READ" | "WRITE" | null | undefined;

export interface ProjectResourceGrantIdentity {
  userId: string;
  connectionId: number;
  resourceType: string;
  resourceKey: string;
  accessLevel: ProjectResourceAccessLevel;
}

export interface ProjectResourceIdentity {
  connectionId?: number | string | null;
  resourceType: string;
  resourceKey: string;
}

export interface ProjectContractResourceIdentity extends ProjectResourceIdentity {
  canUseResource?: boolean;
}

export interface ProjectContractIntegrationIdentity {
  connectionId?: number | string | null;
  canUseIntegration?: boolean;
}

const SECONDARY_RESOURCE_TYPES = new Set(["CUSTOMER", "PROPERTY", "SITE"]);

const normalize = (value: unknown) => String(value ?? "").trim();
const normalizeResourceType = (value: unknown) =>
  normalize(value).toUpperCase();

export const getProjectResourceIdentityKey = (
  resource: ProjectResourceIdentity,
) =>
  `${normalize(resource.connectionId)}:${normalizeResourceType(resource.resourceType)}:${normalize(resource.resourceKey)}`;

const resourceIdentity = getProjectResourceIdentityKey;

export type ProjectResourceSecondaryColumn =
  | { kind: "access"; access: ProjectResourceUiAccess }
  | { kind: "brand"; brandDisplay: string };

export const resolveProjectResourceSecondaryColumn = (
  isProjectScope: boolean,
  accessByResource: Readonly<Record<string, ProjectResourceUiAccess | undefined>>,
  resource: ProjectResourceIdentity,
  brandDisplay: string,
): ProjectResourceSecondaryColumn =>
  isProjectScope
    ? {
        kind: "access",
        access:
          accessByResource[getProjectResourceIdentityKey(resource)] ??
          "no_access",
      }
    : { kind: "brand", brandDisplay };

/**
 * Applies the current Project contract to already-assigned resource rows.
 * Inclusion in a Workspace contract is deliberately ignored: the Project
 * endpoint is the authority for this member in this Business group.
 */
export const applyProjectContractResourceAccess = <
  T extends ProjectContractResourceIdentity,
>(
  assignedResources: T[],
  contractResources: ProjectContractResourceIdentity[],
): T[] => {
  const usableResourceIds = new Set(
    contractResources
      .filter((resource) => resource.canUseResource === true)
      .map(resourceIdentity),
  );

  return assignedResources.map((resource) => ({
    ...resource,
    canUseResource: usableResourceIds.has(resourceIdentity(resource)),
  }));
};

export const isProjectContractIntegrationUsable = (
  integrations: ProjectContractIntegrationIdentity[],
  connectionId: number | string,
): boolean => {
  const normalizedConnectionId = normalize(connectionId);
  return integrations.some(
    (integration) =>
      normalize(integration.connectionId) === normalizedConnectionId &&
      integration.canUseIntegration === true,
  );
};

const toUiAccess = (
  accessLevel: ProjectResourceAccessLevel | undefined,
): ProjectResourceUiAccess => {
  if (accessLevel === "WRITE") return "action";
  if (accessLevel === "READ") return "read";
  return "no_access";
};

/**
 * A Project owner receives the ceiling assigned to the Project. The OAuth
 * connection owner remains WRITE even when that Project ceiling is READ.
 */
export const resolveProjectOwnerAccess = (
  assignmentCeiling: ProjectAssignmentCeiling,
  isConnectionOwner: boolean,
): ProjectResourceUiAccess => {
  if (isConnectionOwner || assignmentCeiling === "WRITE") return "action";
  if (assignmentCeiling === "READ") return "read";
  return "no_access";
};

export const resolveProjectConnectionAccess = ({
  isAssignedToProject,
  isUsableInProjectContract,
  ownerAccess,
  persistedAccess,
}: {
  isAssignedToProject: boolean;
  isUsableInProjectContract: boolean;
  ownerAccess: ProjectResourceUiAccess;
  persistedAccess: ProjectResourceUiAccess;
}): ProjectResourceUiAccess => {
  if (!isAssignedToProject || !isUsableInProjectContract) return "no_access";
  return ownerAccess !== "no_access" ? ownerAccess : persistedAccess;
};

export const resolveProjectResourceContractAccess = ({
  isUsableInProjectContract,
  ownerAccess,
  persistedAccess,
}: {
  isUsableInProjectContract: boolean;
  ownerAccess: ProjectResourceUiAccess;
  persistedAccess: ProjectResourceUiAccess;
}): ProjectResourceUiAccess => {
  if (!isUsableInProjectContract) return "no_access";
  return ownerAccess !== "no_access" ? ownerAccess : persistedAccess;
};

/**
 * Resolves a user's effective access inside one Business group. Workspace
 * resource availability is intentionally not considered here: it only says
 * whether the connector can resolve the resource, not whether this Project
 * member may use it.
 */
export const resolveProjectResourceAccess = (
  grants: ProjectResourceGrantIdentity[],
  userId: string | null | undefined,
  resource: ProjectResourceIdentity,
): ProjectResourceUiAccess => {
  const targetUserId = normalize(userId);
  const connectionId = normalize(resource.connectionId);
  const resourceType = normalizeResourceType(resource.resourceType);
  const resourceKey = normalize(resource.resourceKey);
  const exact = grants.find(
    (grant) =>
      normalize(grant.userId) === targetUserId &&
      normalize(grant.connectionId) === connectionId &&
      normalizeResourceType(grant.resourceType) === resourceType &&
      normalize(grant.resourceKey) === resourceKey,
  );

  if (exact || SECONDARY_RESOURCE_TYPES.has(resourceType)) {
    return toUiAccess(exact?.accessLevel);
  }

  const connectionGrant = grants.find(
    (grant) =>
      normalize(grant.userId) === targetUserId &&
      normalize(grant.connectionId) === connectionId &&
      normalizeResourceType(grant.resourceType) === "CONNECTION" &&
      normalize(grant.resourceKey) === connectionId,
  );
  return toUiAccess(connectionGrant?.accessLevel);
};
